/**
 * IBA Access Validation Utilities
 * Provides validation and error handling for IBA access control
 */

import { IBA_ELIGIBLE_KBA_PARTNERS, IBA_ACCESS_LEVELS, IBA_FEATURES } from 'constants/ibaAccess'

/**
 * Validation error types
 */
export const IBA_VALIDATION_ERRORS = {
  INVALID_PROFILE: 'INVALID_PROFILE',
  MISSING_COUNTRY: 'MISSING_COUNTRY',
  MISSING_KBA_PARTNERS: 'MISSING_KBA_PARTNERS',
  INVALID_FEATURE: 'INVALID_FEATURE',
  ACCESS_DENIED: 'ACCESS_DENIED'
}

/**
 * Validate user profile for IBA access
 * @param {Object} profile - User profile object
 * @returns {Object} - Validation result with isValid and error
 */
export const validateProfileForIBAAccess = (profile) => {
  if (!profile) {
    return {
      isValid: false,
      error: IBA_VALIDATION_ERRORS.INVALID_PROFILE,
      message: 'User profile is required for IBA access validation'
    }
  }

  if (!profile.Country) {
    return {
      isValid: false,
      error: IBA_VALIDATION_ERRORS.MISSING_COUNTRY,
      message: 'User country information is required for IBA access validation'
    }
  }

  if (!profile.KbaPartners || !Array.isArray(profile.KbaPartners)) {
    return {
      isValid: false,
      error: IBA_VALIDATION_ERRORS.MISSING_KBA_PARTNERS,
      message: 'User KBA partners information is required for IBA access validation'
    }
  }

  return {
    isValid: true,
    error: null,
    message: 'Profile is valid for IBA access validation'
  }
}

/**
 * Validate IBA feature access
 * @param {Object} profile - User profile object
 * @param {string} feature - IBA feature type
 * @returns {Object} - Validation result with hasAccess and error
 */
export const validateIBAFeatureAccess = (profile, feature) => {
  const profileValidation = validateProfileForIBAAccess(profile)
  if (!profileValidation.isValid) {
    return {
      hasAccess: false,
      error: profileValidation.error,
      message: profileValidation.message
    }
  }

  // Validate feature type
  const validFeatures = Object.values(IBA_FEATURES)
  if (!validFeatures.includes(feature)) {
    return {
      hasAccess: false,
      error: IBA_VALIDATION_ERRORS.INVALID_FEATURE,
      message: `Invalid IBA feature type: ${feature}. Valid features are: ${validFeatures.join(', ')}`
    }
  }

  // Check if user has eligible KBA partners
  const hasEligibleKbaPartner = profile.KbaPartners.some(partner => 
    IBA_ELIGIBLE_KBA_PARTNERS.includes(partner.id)
  )

  if (!hasEligibleKbaPartner) {
    return {
      hasAccess: false,
      error: IBA_VALIDATION_ERRORS.ACCESS_DENIED,
      message: 'User is not affiliated with any IBA-eligible KBA partners'
    }
  }

  // Check country IBA status for assessment features
  if (feature !== IBA_FEATURES.SITES_MANAGEMENT) {
    if (!profile.Country.iba_active) {
      return {
        hasAccess: false,
        error: IBA_VALIDATION_ERRORS.ACCESS_DENIED,
        message: 'User country does not have IBA active status for assessment features'
      }
    }
  }

  return {
    hasAccess: true,
    error: null,
    message: 'User has access to the requested IBA feature'
  }
}

/**
 * Get detailed IBA access information for debugging
 * @param {Object} profile - User profile object
 * @returns {Object} - Detailed access information
 */
export const getIBAAccessDebugInfo = (profile) => {
  const validation = validateProfileForIBAAccess(profile)
  
  if (!validation.isValid) {
    return {
      isValid: false,
      error: validation.error,
      message: validation.message,
      profile: profile ? {
        hasCountry: !!profile.Country,
        hasKbaPartners: !!(profile.KbaPartners && Array.isArray(profile.KbaPartners)),
        countryIbaActive: profile.Country?.iba_active,
        kbaPartnerIds: profile.KbaPartners?.map(p => p.id) || []
      } : null
    }
  }

  const hasEligibleKbaPartner = profile.KbaPartners.some(partner => 
    IBA_ELIGIBLE_KBA_PARTNERS.includes(partner.id)
  )
  const isCountryIBAActive = profile.Country.iba_active === true

  return {
    isValid: true,
    error: null,
    message: 'Profile is valid',
    profile: {
      hasCountry: true,
      hasKbaPartners: true,
      countryIbaActive: isCountryIBAActive,
      kbaPartnerIds: profile.KbaPartners.map(p => p.id),
      eligibleKbaPartnerIds: IBA_ELIGIBLE_KBA_PARTNERS
    },
    access: {
      hasEligibleKbaPartner,
      isCountryIBAActive,
      accessLevel: hasEligibleKbaPartner && isCountryIBAActive ? IBA_ACCESS_LEVELS.FULL : 
                   hasEligibleKbaPartner ? IBA_ACCESS_LEVELS.LIMITED : IBA_ACCESS_LEVELS.NONE
    },
    features: {
      sitesManagement: hasEligibleKbaPartner,
      congregations: hasEligibleKbaPartner && isCountryIBAActive,
      speciesFields: hasEligibleKbaPartner && isCountryIBAActive,
      criteria: hasEligibleKbaPartner && isCountryIBAActive
    }
  }
}

/**
 * Log IBA access validation for debugging
 * @param {Object} profile - User profile object
 * @param {string} feature - IBA feature type
 * @param {string} context - Context where validation is being performed
 */
export const logIBAAccessValidation = (profile, feature, context = 'Unknown') => {
  if (import.meta.env.NODE_ENV === 'development') {
    const debugInfo = getIBAAccessDebugInfo(profile)
    const featureValidation = validateIBAFeatureAccess(profile, feature)
    
    console.group(`🔍 IBA Access Validation - ${context}`)
    console.log('Feature:', feature)
    console.log('Profile Valid:', debugInfo.isValid)
    console.log('Has Access:', featureValidation.hasAccess)
    console.log('Debug Info:', debugInfo)
    if (featureValidation.error) {
      console.warn('Validation Error:', featureValidation.error, featureValidation.message)
    }
    console.groupEnd()
  }
}
